import { ApolloClient } from "apollo-boost"
import withGA from "next-ga"
import App from "next/app"
import React from "react"
import { compose } from "react-apollo"
import { hotjar } from "react-hotjar"
import withEmptyHoc from "../components/hocs/withEmptyHoc"
import withInitApolloClient from "../components/setup/apollo/withInitApolloClient"
import logger from "../server/logger"
import "../styles/style.css"
import "../styles/react-phone-input-2.css"
import { publicEnvVars } from "../utils/config"
import { configureRouterProgressBar, importRouter } from "../utils/router"
import { withBugsnagNextApp } from "../utils/bugsnag/WithBugsnagNextApp"
import { bugsnagConfig } from "../utils/bugsnag/bugsnagConfig"
import MainApp from "../components/hocs/mainApp/MainApp"
import { appWithTranslation } from "next-i18next"
import nextI18NextConfig from "../../next-i18next.config"

const Router = importRouter()

interface Props {
    apolloClient: ApolloClient<any>
    hasError: boolean
}

interface State {
    hasError: boolean
}

class MyApp extends App<Props, State> {
    static async getInitialProps({ Component, ctx }) {
        try {
            let pageProps = {}

            if (Component.getInitialProps) {
                pageProps = await Component.getInitialProps(ctx)
            }

            return { pageProps: pageProps || {} }
        } catch (error) {
            logger.error("Crash in getInitialProps", {
                error: error,
            })
            return {
                hasError: true,
                pageProps: {},
            }
        }
    }

    state = {
        hasError: false,
    }

    componentDidCatch(error, errorInfo) {
        // @ts-ignore
        logger.error("Crash in componentDidCatch", {
            error: error,
            errorInfo: errorInfo,
        })
    }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    shouldShowErrorPage = () => this.props.hasError || this.state.hasError

    componentDidMount() {
        // Init HotJar
        if (publicEnvVars.HOTJAR_ID) {
            setTimeout(() => hotjar.initialize(publicEnvVars.HOTJAR_ID, 6), 0) // defer
        }

        // Display progress-bar during pages transitions
        configureRouterProgressBar()
    }

    render() {
        const { Component, pageProps, apolloClient } = this.props

        return (
            <MainApp
                Component={Component}
                pageProps={pageProps}
                apolloClient={apolloClient}
                showErrorPage={this.shouldShowErrorPage()}
            />
        )
    }
}

const composed = compose(
    withInitApolloClient,
    publicEnvVars.GOOGLE_ANALYTICS_ID ? withGA(publicEnvVars.GOOGLE_ANALYTICS_ID, Router) : withEmptyHoc
)

export default appWithTranslation(withBugsnagNextApp(bugsnagConfig)(composed(MyApp)), nextI18NextConfig)
